<template>
  <Apexchart
    width="100%"
    height="300"
    type="bar"
    :options="chartOptions"
    :series="series"
    @dataPointSelection="onBarClick"
  />
</template>

<script setup>
import { computed } from "vue";
import { themeTypes } from "@/src/constants/themeConstants.js";
import { chartAlignment } from "@/src/constants/chartConstants.js";
import store from "@/src/store/index.js";

const props = defineProps({
  id: { type: String, required: true },
  categories: { type: Array, required: true },
  seriesData: { type: Array, required: true },
  primaryColor: { type: String, default: "#2579EF" },
  stacked: { type: Boolean, default: false },
  alignment: { type: String, default: chartAlignment.VERTICAL },
  leftMargin: { type: Number, default: 40 },
  customTooltipFormatter: { type: Function, default: null },
  showLegend: { type: Boolean, default: true },
  showCurrency: { type: Boolean, default: false },
  currencySymbol: { type: String, default: "$" },
});

const emit = defineEmits(["barClick"]);

const onBarClick = (event, chartContext, config) => {
  emit("barClick", {
    dataPointIndex: config.dataPointIndex,
    seriesIndex: config.seriesIndex,
    config,
  });
};

const generateColors = (seriesData) => {
  const baseColors = [
    props.primaryColor,
    "#A146E8",
    "#48AF7D",
    "#D89435",
    "#AC531C",
  ];

  return seriesData.map((series, index) => {
    if (series.color) {
      return series.color;
    }
    return baseColors[index % baseColors.length];
  });
};

const isHorizontal = computed(
  () => props.alignment === chartAlignment.HORIZONTAL,
);

// Theme support
const effectiveTheme = computed(() => store.getters["theme/getEffectiveTheme"]);

const getMaxValue = () => {
  if (!props.seriesData?.length) return 5;

  let max = 0;
  props.seriesData?.forEach((series) => {
    if (series?.data?.length > 0) {
      const seriesMax = Math.max(...series.data);
      if (seriesMax > max) max = seriesMax;
    }
  });

  return max === 0 ? 5 : max;
};

const chartOptions = computed(() => {
  const maxValue = getMaxValue();
  const tickAmount = Math.min(5, maxValue);

  const options = {
    chart: {
      id: props.id,
      type: "bar",
      stacked: props.stacked,
      toolbar: { show: false },
      events: {
        dataPointSelection: onBarClick,
      },
    },
    dataLabels: {
      enabled: false,
      formatter: function (val) {
        return val;
      },
    },
    colors: generateColors(props.seriesData),
    fill: {
      type: "gradient",
      gradient: {
        shade: effectiveTheme.value === themeTypes.LIGHT ? "light" : "dark",
        type: "vertical",
        shadeIntensity: 0.1,
        inverseColors: effectiveTheme.value !== themeTypes.LIGHT,
        opacityFrom: 0.5,
        opacityTo: 0.2,
        stops: [0, 80],
      },
    },
    grid: {
      show: true,
      position: "back",
      borderColor: "var(--white-opacity-8)",
    },
    legend: {
      show: props.showLegend,
      position: "bottom",
      horizontalAlign: "left",
      fontSize: "12px",
      fontFamily: "Proxima Nova, sans-serif",
      fontWeight: 400,
      markers: {
        size: 5,
        shape: "circle",
        strokeWidth: 0,
        offsetX: -4,
      },
      itemMargin: {
        horizontal: 10,
        vertical: 0,
      },
      onItemClick: {
        toggleDataSeries: true,
      },
      onItemHover: {
        highlightDataSeries: true,
      },
      labels: {
        colors: "var(--secondary-color)",
        useSeriesColors: false,
      },
      offsetX: -16,
      offsetY: 4,
    },
    stroke: {
      width: 2,
      colors: generateColors(props.seriesData),
    },
    tooltip: {
      theme: effectiveTheme.value.toLowerCase(),
      y: {
        formatter: function (value) {
          return props.customTooltipFormatter
            ? props.customTooltipFormatter(value)
            : value;
        },
      },
    },
    plotOptions: {
      bar: {
        horizontal: isHorizontal.value,
        borderRadius: 2,
        columnWidth: handleBarColumnWidth.value,
        borderWidth: 2,
        strokeWidth: 2,
        borderRadiusApplication: "end",
        distributed: false,
        rangeBarOverlap: true,
        rangeBarGroupRows: false,
        hideZeroBarsWhenGrouped: false,
        dataLabels: {
          position: "top",
          maxItems: 100,
          hideOverflowingLabels: true,
        },
      },
    },
  };

  if (isHorizontal.value) {
    const longestCategoryLength = Math.max(
      ...(props.categories?.map((cat) => (cat ? cat.length : 0)) || [0]),
    );
    const maxLabelChars =
      longestCategoryLength > 20 ? 20 : longestCategoryLength;

    const leftMargin = Math.min(
      Math.max(maxLabelChars * 7, 20),
      props.leftMargin,
    );

    options.chart.offsetX = 0;
    options.chart.width = "100%";

    options.grid = {
      ...options.grid,
      padding: {
        left: leftMargin,
        right: 10,
        top: 0,
        bottom: 0,
      },
    };

    options.xaxis = {
      labels: {
        style: {
          fontSize: "11px",
          colors: "var(--secondary-color)",
          fontFamily: "Proxima Nova, sans-serif",
          fontWeight: "400",
        },
        formatter: (value) =>
          props.showCurrency
            ? `${props.currencySymbol}${Math.round(value)}`
            : Math.round(value),
      },
      axisBorder: {
        show: true,
        color: "var(--white-opacity-8)",
      },
      axisTicks: { show: false },
      min: 0,
      max: maxValue,
      tickAmount: tickAmount,
    };

    options.yaxis = {
      labels: {
        style: {
          fontSize: "11px",
          colors: "var(--secondary-color)",
          fontFamily: "Proxima Nova, sans-serif",
          fontWeight: "400",
        },
      },
    };
  } else {
    options.xaxis = {
      categories: props.categories,
      labels: {
        style: {
          fontSize: "11px",
          colors: "var(--secondary-color)",
          textAlign: "center",
          fontFamily: "Proxima Nova, sans-serif",
          fontWeight: "400",
        },
      },
      axisBorder: {
        show: true,
        color: "var(--white-opacity-8)",
      },
      axisTicks: { show: false },
    };
    options.yaxis = {
      labels: {
        style: {
          fontSize: "11px",
          colors: "var(--secondary-color)",
          fontFamily: "Proxima Nova, sans-serif",
          fontWeight: "400",
        },
        formatter: (value) =>
          props.showCurrency
            ? `${props.currencySymbol}${Math.round(value)}`
            : Math.round(value),
      },
      min: 0,
      max: maxValue,
      tickAmount: tickAmount,
      forceNiceScale: true,
    };
  }

  return options;
});

const series = computed(() => {
  if (isHorizontal.value) {
    return (
      props.seriesData?.map((series) => ({
        name: series?.name,
        data:
          props.categories?.map((category, index) => ({
            x: category,
            y: series?.data?.[index],
          })) || [],
      })) || []
    );
  } else {
    return (
      props.seriesData?.map((series) => ({
        name: series?.name,
        data: series?.data || [],
      })) || []
    );
  }
});

const handleBarColumnWidth = computed(() =>
  props.seriesData?.[0]?.data?.length < 3 && props.seriesData?.length < 3
    ? "30%"
    : props.seriesData?.[0]?.data?.length < 3 && props.seriesData?.length < 6
      ? "60%"
      : props.seriesData?.[0]?.data?.length < 6 && props.seriesData?.length < 3
        ? "60%"
        : "90%",
);
</script>
